import { useMemo, useState } from 'react'

/**
 * Client-side sort hook used by every list table that opted into the
 * standardised `<SortableHeader>` affordance.
 *
 * Why client-side: every table on the platform already loads its
 * current page worth of rows into memory (TanStack Query). Sorting
 * that array before render is essentially free, requires zero backend
 * changes, and doesn't touch the existing filter / pagination /
 * search wiring at all. For paginated tables this DOES mean "sort
 * applies to the current page" — acceptable for a v1 because:
 *   • Page sizes are small (10–50 rows).
 *   • Users sort to scan what's in front of them, not to reorder the
 *     full dataset.
 *   • The existing server-side filter narrows the dataset first.
 * If a table later needs full-dataset sort, the SortableHeader's
 * onSort signature already carries the field+direction the backend
 * would need — wiring a `?sort=field:dir` param into the query is a
 * mechanical follow-up that doesn't require touching this hook.
 *
 * Usage:
 *
 *   const { sortedRows, field, direction, setSort } = useSortable(
 *     rows,
 *     { field: 'createdAt', direction: 'desc' },
 *     {
 *       // Per-field cell accessors — return the raw value used for
 *       // comparison. Omit for fields where row[field] is already
 *       // the comparable primitive.
 *       fullName: (r) => r.fullName?.toLowerCase() ?? '',
 *       storage: (r) => r.storageBytes ?? 0,
 *       trial: (r) => (r.trialEndsAt ? new Date(r.trialEndsAt) : null),
 *     },
 *   )
 *
 *   <SortableHeader field="fullName" label="Name" sortField={field}
 *     direction={direction} onSort={setSort} />
 *
 * @param {Array<object>} rows         Source rows (already filtered).
 * @param {{field: string|null, direction: 'asc'|'desc'}|null} initial
 *                                     Initial sort. Pass `null` to
 *                                     start unsorted (preserves the
 *                                     caller's source ordering).
 * @param {Record<string, (row:any)=>any>} accessors  Per-field value
 *                                     extractors. Defaults to
 *                                     `row[field]` for any field not
 *                                     in this map.
 *
 * @returns {{
 *   sortedRows: Array<object>,
 *   field: string|null,
 *   direction: 'asc'|'desc',
 *   setSort: (field: string) => void,
 * }}
 */
export function useSortable(rows, initial = null, accessors = {}) {
  const [field, setField] = useState(initial?.field ?? null)
  const [direction, setDirection] = useState(initial?.direction ?? 'asc')

  /**
   * Toggle / select sort:
   *   • Click the active column → flip asc ↔ desc.
   *   • Click a new column      → start at asc.
   * Spec calls for "first click asc, second click desc" — no
   * tri-state "off" because users sorting a table generally want SOME
   * sort, not unsorted. To revert to the table's natural order, the
   * caller can pass `initial=null` and the hook will leave rows
   * untouched until `setSort` is first called.
   */
  function setSort(nextField) {
    if (nextField === field) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
      return
    }
    setField(nextField)
    setDirection('asc')
  }

  /** Default comparator: stable null-sink + locale-aware string
   *  compare + numeric subtraction for everything else. The
   *  null-sink rule means "rows with no value for this column always
   *  appear at the bottom" regardless of asc/desc — generally the
   *  intuitive choice (a row without a trial date shouldn't push
   *  ahead of one with a real date). */
  function defaultCompare(a, b) {
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1
    if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime()
    if (typeof a === 'number' && typeof b === 'number') return a - b
    if (typeof a === 'boolean' && typeof b === 'boolean') {
      // false < true in numeric coercion; we want true-first when asc.
      return (a === b ? 0 : a ? -1 : 1)
    }
    return String(a).localeCompare(String(b), 'en', { sensitivity: 'base' })
  }

  const sortedRows = useMemo(() => {
    if (!field) return rows
    const get = accessors[field] ?? ((row) => row?.[field])
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = get(a)
      const bv = get(b)
      const c = defaultCompare(av, bv)
      return direction === 'asc' ? c : -c
    })
    return copy
    // `accessors` and `rows` ref-stability is the caller's concern.
    // Most pages declare `accessors` inline — it's a fresh object per
    // render. That's fine; the sort itself is O(n log n) which is
    // trivial at table-page sizes (typically < 100 rows). Adding a
    // deps-by-shape memoisation here would just add complexity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, field, direction])

  return { sortedRows, field, direction, setSort }
}
